/**
 * Eric 2026-06-25 (iter126 P1) — Mode validation rapide (Tinder-style).
 *
 * Page de validation des KU "pending_review" en mode swipe. Affiche les
 * brouillons triés par similarité sémantique (parcours fluide entre
 * sujets connexes). L'utilisateur swipe → pour Approuver, ← pour
 * Rejeter ; raccourcis clavier (← / →).
 *
 * Gating : canEditContent (Concepteur+). Backend gate require_editor.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, X, ArrowLeft, ArrowRight, SpinnerGap, Sparkle,
  Lightning, Trophy, Question,
} from "@phosphor-icons/react";

import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import { API_BASE as API } from "../lib/apiBase";
import { Button } from "../components/ui/button";

const TYPE_LABEL = {
  qa: { fr: "Q&R", en: "Q&A" },
  concept: { fr: "Concept", en: "Concept" },
  script: { fr: "Script", en: "Script" },
  procedure: { fr: "Procédure", en: "Procedure" },
};

export default function ValidationRapidePage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const canValidate = canEditContent(user);

  const [queue, setQueue] = useState([]);
  const [total, setTotal] = useState(0);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState(""); // type filter
  // Vincent #397 (Eric 2026-06-29 SPEC STRICK) — Tri client-side (par-dessus
  // le tri par similarité du backend). Option "similarity" garde le fluide.
  const [sortBy, setSortBy] = useState("similarity"); // similarity|name_asc|name_desc|created_asc|created_desc|updated_asc|updated_desc
  const [stats, setStats] = useState({ approved: 0, rejected: 0 });
  const [swipeDir, setSwipeDir] = useState(null); // 'left' | 'right' | null pour animation

  const cardRef = useRef(null);
  const dragStart = useRef(null);
  const [dragX, setDragX] = useState(0);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/agent/validation-queue/by-similarity?limit=100${filter ? `&ku_type=${filter}` : ""}`;
      const { data } = await axios.get(url, { withCredentials: true });
      const items = data.queue || [];
      // Vincent #397 (Eric 2026-06-29 SPEC STRICK) — Tri client-side après chargement.
      const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), "fr", { sensitivity: "base" });
      const ts = (d) => (d ? new Date(d).getTime() : 0);
      const titleOf = (k) => (k.question || k.title || k.term || k.objection || "");
      const sorted = [...items];
      switch (sortBy) {
        case "name_asc": sorted.sort((a, b) => cmpStr(titleOf(a), titleOf(b))); break;
        case "name_desc": sorted.sort((a, b) => cmpStr(titleOf(b), titleOf(a))); break;
        case "created_asc": sorted.sort((a, b) => ts(a.created_at) - ts(b.created_at)); break;
        case "created_desc": sorted.sort((a, b) => ts(b.created_at) - ts(a.created_at)); break;
        case "updated_asc": sorted.sort((a, b) => ts(a.updated_at || a.created_at) - ts(b.updated_at || b.created_at)); break;
        case "updated_desc": sorted.sort((a, b) => ts(b.updated_at || b.created_at) - ts(a.updated_at || a.created_at)); break;
        default: break; // "similarity" → garde l'ordre du backend
      }
      setQueue(sorted);
      setTotal(data.total || 0);
      setIdx(0);
    } catch (e) {
      toast.error(
        lang === "fr"
          ? `Erreur de chargement : ${e.response?.data?.detail || e.message}`
          : `Loading error: ${e.response?.data?.detail || e.message}`
      );
    }
    setLoading(false);
  }, [filter, sortBy, lang]);

  useEffect(() => {
    if (canValidate) loadQueue();
  }, [canValidate, loadQueue]);

  const currentKu = queue[idx];

  const handleDecide = useCallback(
    async (decision) => {
      if (!currentKu || processing) return;
      setProcessing(true);
      setSwipeDir(decision === "approve" ? "right" : "left");
      try {
        await axios.post(
          `${API}/api/agent/draft-queue/${currentKu.id}/decide`,
          { decision },
          { withCredentials: true }
        );
        setStats((s) => ({
          ...s,
          [decision === "approve" ? "approved" : "rejected"]:
            s[decision === "approve" ? "approved" : "rejected"] + 1,
        }));
        // Animation puis avancer
        setTimeout(() => {
          setIdx((i) => i + 1);
          setSwipeDir(null);
          setDragX(0);
          setProcessing(false);
        }, 220);
      } catch (e) {
        toast.error(
          lang === "fr"
            ? `Erreur : ${e.response?.data?.detail || e.message}`
            : `Error: ${e.response?.data?.detail || e.message}`
        );
        setSwipeDir(null);
        setDragX(0);
        setProcessing(false);
      }
    },
    [currentKu, processing, lang]
  );

  // Raccourcis clavier ← / →
  useEffect(() => {
    if (!canValidate) return undefined;
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight") handleDecide("approve");
      else if (e.key === "ArrowLeft") handleDecide("reject");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canValidate, handleDecide]);

  // Drag handlers
  const onMouseDown = (e) => {
    if (processing) return;
    dragStart.current = e.clientX;
  };
  const onMouseMove = (e) => {
    if (dragStart.current == null) return;
    setDragX(e.clientX - dragStart.current);
  };
  const onMouseUp = () => {
    if (dragStart.current == null) return;
    const dx = dragX;
    dragStart.current = null;
    if (dx > 120) handleDecide("approve");
    else if (dx < -120) handleDecide("reject");
    else setDragX(0);
  };

  if (!canValidate) {
    return (
      <div className="p-8 text-center text-zinc-500" data-testid="validation-rapide-forbidden">
        <p>{lang === "fr" ? "Accès réservé aux éditeurs de contenu." : "Editor access only."}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="validation-rapide-loading">
        <SpinnerGap size={32} className="mx-auto animate-spin text-zinc-400" />
      </div>
    );
  }

  if (queue.length === 0 || idx >= queue.length) {
    return (
      <div
        className="max-w-2xl mx-auto p-8 text-center space-y-4"
        data-testid="validation-rapide-empty"
      >
        <Trophy size={64} className="mx-auto text-amber-400" weight="duotone" />
        <h2 className="text-2xl font-semibold tracking-tight font-['Outfit']">
          {lang === "fr" ? "File vide — bravo !" : "Empty queue — well done!"}
        </h2>
        <p className="text-zinc-500">
          {lang === "fr"
            ? `Tu as traité ${stats.approved} approbation(s) et ${stats.rejected} rejet(s) cette session.`
            : `You handled ${stats.approved} approval(s) and ${stats.rejected} rejection(s) this session.`}
        </p>
        {/* Vincent #397 (Eric 2026-06-29 SPEC STRICK) — Filtre + tri exposés
            aussi sur l'écran vide pour permettre de changer le type/ordre et
            recharger. */}
        <div className="flex flex-wrap justify-center gap-2 pt-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-xs border border-zinc-200 rounded px-2 py-1 bg-white"
            data-testid="validation-rapide-filter"
          >
            <option value="">{lang === "fr" ? "Tous types" : "All types"}</option>
            <option value="qa">Q&R</option>
            <option value="concept">{lang === "fr" ? "Concepts" : "Concepts"}</option>
            <option value="script">Scripts</option>
            <option value="procedure">{lang === "fr" ? "Procédures" : "Procedures"}</option>
            <option value="objection">{lang === "fr" ? "Objections" : "Objections"}</option>
            <option value="rebuttal">{lang === "fr" ? "Réfutations" : "Rebuttals"}</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs border border-zinc-200 rounded px-2 py-1 bg-white"
            data-testid="validation-rapide-sort"
          >
            <option value="similarity">{lang === "fr" ? "Similarité sémantique" : "Semantic similarity"}</option>
            <option value="name_asc">{lang === "fr" ? "Ordre alphabétique (A → Z)" : "Alphabetical (A → Z)"}</option>
            <option value="name_desc">{lang === "fr" ? "Ordre alphabétique (Z → A)" : "Alphabetical (Z → A)"}</option>
            <option value="created_asc">{lang === "fr" ? "Date de création (croissante)" : "Created (asc)"}</option>
            <option value="created_desc">{lang === "fr" ? "Date de création (décroissante)" : "Created (desc)"}</option>
            <option value="updated_asc">{lang === "fr" ? "Date de modification (croissante)" : "Modified (asc)"}</option>
            <option value="updated_desc">{lang === "fr" ? "Date de modification (décroissante)" : "Modified (desc)"}</option>
          </select>
        </div>
        <Button onClick={loadQueue} data-testid="validation-rapide-reload">
          {lang === "fr" ? "Recharger la file" : "Reload queue"}
        </Button>
      </div>
    );
  }

  const remaining = queue.length - idx;
  const ku = currentKu;
  const content = ku.content || {};
  const typeLbl = TYPE_LABEL[ku.type]?.[lang] || ku.type;

  return (
    <div
      className="max-w-2xl mx-auto p-6 space-y-6"
      data-testid="validation-rapide-page"
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">
          <Sparkle size={20} weight="fill" className="inline mr-2 text-amber-500" />
          {lang === "fr" ? "File de validation rapide" : "Fast validation queue"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {lang === "fr"
            ? `${remaining} restant(s) • triés par similarité sémantique`
            : `${remaining} remaining • sorted by semantic similarity`}
        </p>
      </div>

      {/* Vincent #402 (Eric 2026-07-06 21h50 SPEC STRICK — 5e passe) — Les
          2 menus déroulants doivent être VISIBLES ENTRE le titre et la carte.
          Encadrés + labels explicites + largeur généreuse pour être repérables
          au 1er coup d'œil (Vincent : "l'utilisateur ne le voit pas"). */}
      <div className="p-3 bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-700 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="validation-rapide-filters-bar">
        <label className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">
          <span className="block mb-1">{lang === "fr" ? "Filtrer par type de connaissance" : "Filter by knowledge type"}</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full text-sm border border-zinc-300 dark:border-zinc-600 rounded px-3 py-2 bg-white dark:bg-zinc-800"
            data-testid="validation-rapide-filter"
          >
            <option value="">{lang === "fr" ? "Tous les types" : "All types"}</option>
            <option value="qa">Q&R</option>
            <option value="script">{lang === "fr" ? "Scripts de vente" : "Sales scripts"}</option>
            <option value="procedure">{lang === "fr" ? "Procédures" : "Procedures"}</option>
            <option value="objection">{lang === "fr" ? "Objections" : "Objections"}</option>
            <option value="rebuttal">{lang === "fr" ? "Réfutations" : "Rebuttals"}</option>
            <option value="concept">{lang === "fr" ? "Concepts" : "Concepts"}</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">
          <span className="block mb-1">{lang === "fr" ? "Trier par" : "Sort by"}</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full text-sm border border-zinc-300 dark:border-zinc-600 rounded px-3 py-2 bg-white dark:bg-zinc-800"
            data-testid="validation-rapide-sort"
          >
            <option value="similarity">{lang === "fr" ? "Similarité sémantique (défaut)" : "Semantic similarity (default)"}</option>
            <option value="name_asc">{lang === "fr" ? "Ordre alphabétique (A → Z)" : "Alphabetical (A → Z)"}</option>
            <option value="name_desc">{lang === "fr" ? "Ordre alphabétique (Z → A)" : "Alphabetical (Z → A)"}</option>
            <option value="created_asc">{lang === "fr" ? "Date de création (croissante)" : "Created (asc)"}</option>
            <option value="created_desc">{lang === "fr" ? "Date de création (décroissante)" : "Created (desc)"}</option>
            <option value="updated_asc">{lang === "fr" ? "Date de modification (croissante)" : "Modified (asc)"}</option>
            <option value="updated_desc">{lang === "fr" ? "Date de modification (décroissante)" : "Modified (desc)"}</option>
          </select>
        </label>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3 text-center text-xs">
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-2">
          <p className="text-teal-700 font-semibold text-base">{stats.approved}</p>
          <p className="text-teal-600">{lang === "fr" ? "Approuvées" : "Approved"}</p>
        </div>
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-2">
          <p className="text-zinc-700 font-semibold text-base">{remaining}</p>
          <p className="text-zinc-500">{lang === "fr" ? "Restantes" : "Remaining"}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-2">
          <p className="text-red-700 font-semibold text-base">{stats.rejected}</p>
          <p className="text-red-600">{lang === "fr" ? "Rejetées" : "Rejected"}</p>
        </div>
      </div>

      {/* Card */}
      <div
        ref={cardRef}
        onMouseDown={onMouseDown}
        className={`relative bg-white border-2 border-zinc-200 rounded-2xl p-6 shadow-lg cursor-grab active:cursor-grabbing transition-transform ${
          swipeDir === "right"
            ? "translate-x-[500px] rotate-12 opacity-0"
            : swipeDir === "left"
            ? "-translate-x-[500px] -rotate-12 opacity-0"
            : ""
        }`}
        style={swipeDir ? {} : { transform: `translateX(${dragX}px) rotate(${dragX / 30}deg)` }}
        data-testid="validation-rapide-card"
      >
        {/* Swipe hints overlay */}
        {dragX > 50 && (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-teal-500 text-white font-bold text-sm rotate-12 shadow">
            ✓ {lang === "fr" ? "APPROUVER" : "APPROVE"}
          </div>
        )}
        {dragX < -50 && (
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-red-500 text-white font-bold text-sm -rotate-12 shadow">
            ✕ {lang === "fr" ? "REJETER" : "REJECT"}
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 mb-3 text-[11px]">
          <span className="px-2 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7] font-semibold">
            {typeLbl}
          </span>
          {ku.video_title && (
            <span className="text-zinc-500 truncate">{ku.video_title}</span>
          )}
          {typeof ku._sim_to_prev === "number" && ku._sim_to_prev > 0.5 && (
            <span className="ml-auto px-2 py-0.5 rounded bg-amber-50 text-amber-700">
              <Lightning size={10} className="inline -mt-0.5" /> {Math.round(ku._sim_to_prev * 100)}%
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="text-lg font-semibold mb-3 leading-snug" data-testid="validation-rapide-title">
          {ku.title || (content.question || "—")}
        </h2>

        {/* Content */}
        {ku.type === "qa" && (
          <>
            {content.question && (
              <div className="mb-3 p-3 bg-zinc-50 rounded-lg">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                  <Question size={10} className="inline -mt-0.5 mr-1" />
                  {lang === "fr" ? "Question" : "Question"}
                </p>
                <p className="text-sm text-zinc-800">{content.question}</p>
              </div>
            )}
            {content.answer && (
              <div className="p-3 bg-teal-50 rounded-lg">
                <p className="text-[10px] uppercase tracking-wider text-teal-600 mb-1">
                  {lang === "fr" ? "Réponse" : "Answer"}
                </p>
                <p className="text-sm text-zinc-800 whitespace-pre-wrap">{content.answer}</p>
              </div>
            )}
          </>
        )}
        {ku.type === "concept" && (
          <div className="p-3 bg-zinc-50 rounded-lg">
            <p className="text-sm text-zinc-800 whitespace-pre-wrap">
              {content.definition || content.description || JSON.stringify(content)}
            </p>
          </div>
        )}
        {(ku.type === "script" || ku.type === "procedure") && (
          <div className="p-3 bg-zinc-50 rounded-lg max-h-64 overflow-y-auto">
            <p className="text-sm text-zinc-800 whitespace-pre-wrap font-mono">
              {content.text || content.script || content.steps?.map((s, i) => `${i + 1}. ${s}`).join("\n") || JSON.stringify(content)}
            </p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 justify-center">
        <Button
          variant="outline"
          onClick={() => handleDecide("reject")}
          disabled={processing}
          className="flex-1 gap-2 border-red-300 text-red-700 hover:bg-red-50"
          data-testid="validation-rapide-reject-btn"
        >
          <ArrowLeft size={16} weight="bold" />
          <X size={16} weight="bold" />
          {lang === "fr" ? "Rejeter" : "Reject"}
        </Button>
        <Button
          onClick={() => handleDecide("approve")}
          disabled={processing}
          className="flex-1 gap-2 bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="validation-rapide-approve-btn"
        >
          <Check size={16} weight="bold" />
          {lang === "fr" ? "Approuver" : "Approve"}
          <ArrowRight size={16} weight="bold" />
        </Button>
      </div>

      <p className="text-center text-[11px] text-zinc-400">
        {lang === "fr"
          ? "💡 Raccourcis : ← rejeter • → approuver • ou glissez la carte"
          : "💡 Shortcuts: ← reject • → approve • or drag the card"}
      </p>
    </div>
  );
}
